import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import OverviewSection    from './sections/OverviewSection';
import DoctorsSection     from './sections/DoctorsSection';
import DoctorPayoutsSection from './sections/DoctorPayoutsSection';
import FinanceSection from './sections/FinanceSection';
import UsersSection       from './sections/UsersSection';
import AgendaPage         from './AgendaPage';
import MedicalRecordsPage from './MedicalRecordsPage';
import PaymentsPage     from './PaymentsPage';
import AuditLogsPage    from './AuditLogsPage';
import '../../styles/agenda.css';
import {
  LayoutDashboard, Users, Stethoscope, CalendarDays,
  CreditCard, FileText, LogOut, Shield, Menu, X, ScrollText, Wallet, Receipt,
} from 'lucide-react';

// ─── Sidebar ─────────────────────────────────────────────────────────────────

const NAV_ITEMS = [
  { id: 'overview', label: 'Resumen',           Icon: LayoutDashboard, available: true  },
  { id: 'doctors',  label: 'Doctores',           Icon: Stethoscope,     available: true  },
  { id: 'users',    label: 'Usuarios',           Icon: Users,           available: true  },
  { id: 'agenda',   label: 'Agenda y Citas',     Icon: CalendarDays,    available: true  },
  { id: 'records',  label: 'Historias Clínicas', Icon: FileText,        available: true  },
  { id: 'payments', label: 'Pagos',              Icon: CreditCard,      available: true  },
  { id: 'payouts',  label: 'Pagos a médicos',    Icon: Wallet,          available: true  },
  { id: 'finance',  label: 'Gastos y balance',   Icon: Receipt,         available: true  },
  { id: 'audit',    label: 'Auditoría',          Icon: ScrollText,      available: true  },
];

const Sidebar = ({ active, onSelect, onLogout, user, collapsed, onToggle }) => (
  <aside className={`fixed inset-y-0 left-0 z-30 flex flex-col bg-white border-r border-gray-200
    transition-all duration-200 ${collapsed ? 'w-16' : 'w-56'}`}>

    <div className="flex items-center gap-3 px-4 h-16 border-b border-gray-200">
      <div className="flex-shrink-0 w-8 h-8 bg-teal-600 rounded-lg flex items-center justify-center">
        <Shield className="w-4 h-4 text-white" />
      </div>
      {!collapsed && (
        <div className="min-w-0">
          <p className="text-sm font-bold text-gray-900 truncate">DocEnLinea</p>
          <p className="text-xs text-gray-400">Admin</p>
        </div>
      )}
      <button onClick={onToggle}
        className="ml-auto p-1 rounded text-gray-400 hover:text-gray-700 hover:bg-gray-100"
        aria-label={collapsed ? 'Expandir sidebar' : 'Colapsar sidebar'}>
        {collapsed ? <Menu className="w-4 h-4" /> : <X className="w-4 h-4" />}
      </button>
    </div>

    <nav className="flex-1 py-4 overflow-y-auto">
      {NAV_ITEMS.map(({ id, label, Icon, available, eta }) => (
        <button key={id}
          onClick={() => available && onSelect(id)}
          title={collapsed ? label : undefined}
          className={`w-full flex items-center gap-3 px-4 py-2.5 text-sm transition-colors
            ${!available
              ? 'text-gray-300 cursor-not-allowed'
              : active === id
              ? 'bg-teal-50 text-teal-700 font-medium border-r-2 border-teal-600'
              : 'text-gray-600 hover:bg-gray-50 hover:text-gray-900'}`}>
          <Icon className="w-4 h-4 flex-shrink-0" />
          {!collapsed && <span className="flex-1 text-left truncate">{label}</span>}
          {!collapsed && !available && eta && (
            <span className="text-xs text-gray-300">{eta}</span>
          )}
        </button>
      ))}
    </nav>

    <div className="border-t border-gray-200 p-4">
      {!collapsed && (
        <div className="mb-3">
          <p className="text-xs font-medium text-gray-800 truncate">
            {user?.profile?.firstName} {user?.profile?.lastName}
          </p>
          <p className="text-xs text-gray-400 truncate">{user?.email}</p>
        </div>
      )}
      <button onClick={onLogout} title="Cerrar sesión"
        className="w-full flex items-center gap-2 text-xs text-gray-500 hover:text-red-600
          hover:bg-red-50 rounded-lg px-3 py-2 transition-colors">
        <LogOut className="w-4 h-4 flex-shrink-0" />
        {!collapsed && <span>Cerrar sesión</span>}
      </button>
    </div>
  </aside>
);

// ─── Main ─────────────────────────────────────────────────────────────────────
const SECTION_LABELS = {
  overview: 'Resumen General',
  doctors:  'Gestión de Médicos',
  users:    'Gestión de Usuarios',
  agenda:   'Agenda y Citas',
  records:  'Historias Clínicas',
  payments: 'Pagos',
  payouts:  'Pagos a Médicos',
  finance:  'Gastos y Balance',
  audit:    'Auditoría de Accesos',
};

function DashboardAdmin() {
  const { user, logout } = useAuth();
  const navigate         = useNavigate();
  const [section,           setSection]           = useState('overview');
  const [sidebarCollapsed,  setSidebarCollapsed]  = useState(false);

  const handleLogout = () => { logout(); navigate('/'); };

  const renderSection = () => {
    switch (section) {
      case 'overview': return <OverviewSection />;
      case 'doctors':  return <DoctorsSection />;
      case 'users':    return <UsersSection />;
      case 'agenda':   return <AgendaPage />;
      case 'records':  return <MedicalRecordsPage />;
      case 'payments': return <PaymentsPage />;
      case 'payouts':  return <DoctorPayoutsSection />;
      case 'finance':  return <FinanceSection />;
      case 'audit':    return <AuditLogsPage />;
      default:         return <OverviewSection />;
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 flex">
      <Sidebar
        active={section}
        onSelect={setSection}
        onLogout={handleLogout}
        user={user}
        collapsed={sidebarCollapsed}
        onToggle={() => setSidebarCollapsed(c => !c)}
      />
      <div className={`flex-1 flex flex-col min-h-screen transition-all duration-200 ${
        sidebarCollapsed ? 'ml-16' : 'ml-56'
      }`}>
        <header className="h-16 bg-white border-b border-gray-200 flex items-center px-6">
          <h1 className="text-base font-semibold text-gray-800">
            {SECTION_LABELS[section] || 'Resumen General'}
          </h1>
        </header>
        <main className="flex-1 p-6 max-w-6xl w-full">
          {renderSection()}
        </main>
      </div>
    </div>
  );
}

export default DashboardAdmin;